import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetInventoryVendor,
  useListLedgerPaymentAccounts,
  useCreateBillPayment,
  getGetInventoryVendorQueryKey,
  getGetVendorBillQueryKey,
  getListVendorBillsQueryKey,
  getListBillPaymentsQueryKey,
} from "@workspace/api-client-react";
import type { VendorBillDetail, BillPayment, PaymentAccount, PaymentSourceKind } from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { inr, today, round2 } from "@/lib/format";
import { uploadPaymentProof } from "@/lib/upload";
import { FileField } from "@/components/expenses/file-field";

const PAYMENT_SOURCE_KIND_LABEL: Record<PaymentSourceKind, string> = {
  bank: "Bank",
  cash: "Cash",
  petty_cash: "Petty cash",
  partner: "Partner",
};

const PAYMENT_METHODS = ["Cash", "UPI", "Bank_Transfer", "Card", "Cheque"] as const;

interface PaymentForm {
  paymentDate: string;
  grossAmount: string;
  paymentAccountId: string; // "" = none chosen — A6's whole point
  tdsAmount: string;
  tdsRate: number | null;
  tdsCategory: string;
  paymentMethod: string;
  paymentReference: string;
  paymentProofUrl: string | null;
}

function emptyForm(outstanding: number): PaymentForm {
  return {
    paymentDate: today(),
    grossAmount: outstanding > 0 ? String(outstanding) : "",
    paymentAccountId: "",
    tdsAmount: "",
    tdsRate: null,
    tdsCategory: "",
    paymentMethod: "",
    paymentReference: "",
    paymentProofUrl: null,
  };
}

export interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bill: VendorBillDetail | null;
}

export function PaymentDialog({ open, onOpenChange, bill }: PaymentDialogProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const vendorId = bill?.vendorId ?? 0;
  const { data: vendor } = useGetInventoryVendor(vendorId, {
    query: { enabled: !!bill, queryKey: getGetInventoryVendorQueryKey(vendorId) },
  });
  // A6 — sourced from the API's own funding-source list. Never a literal
  // Bank/Cash pair hard-coded in this component.
  const { data: paymentAccounts } = useListLedgerPaymentAccounts();
  const createMut = useCreateBillPayment();

  const outstanding = bill ? round2(bill.grossTotal - bill.payments.reduce((s, p) => s + p.grossAmount, 0)) : 0;
  const [form, setForm] = useState<PaymentForm>(emptyForm(outstanding));

  useEffect(() => {
    if (!open || !bill) return;
    const out = round2(bill.grossTotal - bill.payments.reduce((s, p) => s + p.grossAmount, 0));
    const tdsRate = vendor?.tdsApplicable && vendor.tdsRate != null ? vendor.tdsRate : null;
    setForm({
      ...emptyForm(out),
      tdsAmount: tdsRate != null ? String(round2((out * tdsRate) / 100)) : "",
      tdsRate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bill?.id, vendor?.tdsApplicable, vendor?.tdsRate]);

  const grouped = useMemo(() => {
    const groups = new Map<PaymentSourceKind, PaymentAccount[]>();
    for (const a of paymentAccounts ?? []) {
      const list = groups.get(a.paymentSourceKind) ?? [];
      list.push(a);
      groups.set(a.paymentSourceKind, list);
    }
    return groups;
  }, [paymentAccounts]);

  const selectedAccount = (paymentAccounts ?? []).find((a) => String(a.id) === form.paymentAccountId) ?? null;
  const isPartner = selectedAccount?.paymentSourceKind === "partner";

  // A1 — TDS on a personally-paid bill is out of scope (the server 400s):
  // force it to zero the moment a partner-kind account is chosen, and keep
  // the field disabled while it's selected.
  useEffect(() => {
    if (isPartner && form.tdsAmount !== "0" && form.tdsAmount !== "") {
      setForm((f) => ({ ...f, tdsAmount: "0" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartner]);

  if (!bill) return null;

  const grossNum = Number(form.grossAmount) || 0;
  const tdsNum = isPartner ? 0 : form.tdsAmount === "" ? 0 : Number(form.tdsAmount) || 0;
  const bankAmount = round2(grossNum - tdsNum);
  const canSave = form.paymentAccountId !== "" && grossNum > 0 && !createMut.isPending;

  async function handleSave() {
    if (!bill || !canSave) return;
    try {
      const result = await createMut.mutateAsync({
        id: bill.id,
        data: {
          paymentDate: form.paymentDate,
          grossAmount: grossNum,
          tdsAmount: tdsNum,
          tdsRate: form.tdsRate ?? null,
          tdsCategory: form.tdsCategory || null,
          paymentAccountId: Number(form.paymentAccountId),
          paymentMethod: form.paymentMethod || null,
          paymentReference: form.paymentReference || null,
          paymentProofUrl: form.paymentProofUrl,
        },
      });
      // On a 202 the wire body is `{quarantined, quarantineId, reason}`, NOT
      // a BillPayment — 57-09's generated contract only declares the 201
      // shape (same documented gap as the approve/backfill endpoints).
      const withQ = result as BillPayment & { quarantined?: boolean; reason?: string };
      qc.invalidateQueries({ queryKey: getGetVendorBillQueryKey(bill.id) });
      qc.invalidateQueries({ queryKey: getListVendorBillsQueryKey() });
      qc.invalidateQueries({ queryKey: getListBillPaymentsQueryKey(bill.id) });
      if (withQ.quarantined) {
        toast({ title: "Payment recorded — posting quarantined", description: withQ.reason, variant: "destructive" });
      } else {
        toast({ title: "Payment recorded." });
      }
      onOpenChange(false);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not record payment.", variant: "destructive" });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record payment — Bill #{bill.id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold">Payment date</label>
              <Input type="date" value={form.paymentDate} onChange={(e) => setForm((f) => ({ ...f, paymentDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold">
                Amount ₹ <span className="text-destructive">*</span>
              </label>
              <Input
                type="number"
                step="0.01"
                data-testid="payment-amount"
                value={form.grossAmount}
                onChange={(e) => setForm((f) => ({ ...f, grossAmount: e.target.value }))}
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Outstanding: {inr(outstanding)}</p>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold">
              Paid from <span className="text-destructive">*</span>
            </label>
            <select
              data-testid="payment-account-select"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
              value={form.paymentAccountId}
              onChange={(e) => setForm((f) => ({ ...f, paymentAccountId: e.target.value }))}
            >
              <option value="">Choose a funding account…</option>
              {(["bank", "cash", "petty_cash", "partner"] as PaymentSourceKind[]).map((kind) => {
                const accountsOfKind = grouped.get(kind);
                if (!accountsOfKind || accountsOfKind.length === 0) return null;
                return (
                  <optgroup key={kind} label={PAYMENT_SOURCE_KIND_LABEL[kind]}>
                    {accountsOfKind.map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.code} · {a.name}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            {(paymentAccounts ?? []).length <= 4 && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Only the seeded starter accounts exist so far.{" "}
                <a href="/ledger?tab=master-data" className="underline">
                  Add your bank accounts
                </a>{" "}
                before relying on this list day to day.
              </p>
            )}
            {isPartner && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Recorded as paid by a partner personally. Company bank is untouched; the amount shows as owed to the
                partner until reimbursed.
              </p>
            )}
          </div>

          <div className="rounded-md border p-3 space-y-2">
            <p className="text-xs font-semibold">TDS withheld</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">TDS amount ₹</label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="tds-amount"
                  disabled={isPartner}
                  value={form.tdsAmount}
                  onChange={(e) => setForm((f) => ({ ...f, tdsAmount: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">TDS rate %</label>
                <Input
                  type="number"
                  step="0.01"
                  disabled={isPartner}
                  value={form.tdsRate ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, tdsRate: e.target.value ? Number(e.target.value) : null }))}
                />
              </div>
            </div>
            {isPartner && (
              <p className="text-[11px] text-muted-foreground">
                TDS on a personally-paid bill is out of scope this phase — waive it or choose a non-partner account.
              </p>
            )}
            <p data-testid="payment-split" className="text-sm font-mono tabular-nums">
              {inr(bankAmount)} + TDS {inr(tdsNum)} = {inr(grossNum)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold">Method</label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                value={form.paymentMethod}
                onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
              >
                <option value="">—</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Reference</label>
              <Input
                placeholder="UTR / txn id / cheque no"
                value={form.paymentReference}
                onChange={(e) => setForm((f) => ({ ...f, paymentReference: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold">Payment proof</label>
            <FileField value={form.paymentProofUrl} onChange={(u) => setForm((f) => ({ ...f, paymentProofUrl: u }))} uploadFn={uploadPaymentProof} label="Payment proof" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button data-testid="payment-save" onClick={handleSave} disabled={!canSave}>
            {createMut.isPending ? "Saving…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
